import { Component, TemplateRef, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ContentTree, Content, ContentType } from 'src/app/models/documents/folder.model';
import { DocumentService } from 'src/app/services/document.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';

@Component({
  selector: 'app-breadcrumb-input',
  templateUrl: './breadcrumb-input.component.html',
  styleUrls: ['./breadcrumb-input.component.scss'],
  standalone: false
})
export class BreadcrumbInputComponent implements OnInit {
  currentBreadcrumbs: Content[] = [];

  currentChildren: Content[] = []; 

  treeStructure!: Content;

  newFolderName = '';
  createFolderError = '';

  constructor(
    private dialog: MatDialog, 
    private documentService: DocumentService,
    public languageService: LanguageService,
    private frontendLogger: FrontendLoggerService
  ) {}

  ngOnInit(): void {
    this.getFolderStructure().subscribe();
  }

  getFolderStructure()
  {
    return this.documentService
    .getFolderTreeStructure()
    .pipe(
      tap( (x:ContentTree) => {
          const root = Array.isArray(x?.content) ? x.content[0] : x?.content;
          if (!root) {
            // No hay estructura; reiniciar a estado seguro
            this.treeStructure = undefined as any;
            this.currentBreadcrumbs = [];
            this.currentChildren = [];
            this.frontendLogger.info('[BreadcrumbInput] No se encontró estructura de folders');
            return;
          }
          // Normalizar y filtrar recursivamente SOLO carpetas
          this.treeStructure = this.filterFoldersOnly(root);
          this.currentBreadcrumbs = [this.treeStructure].filter(Boolean) as Content[];
          const children = Array.isArray(this.treeStructure?.children) ? this.treeStructure.children : [];
          this.currentChildren = children
            .filter(ch => this.isFolderType(ch?.type))
            .map((x:Content) => ({
              id: x?.id,
              name: x?.name,
              createdAt: x?.createdAt,
              type: x?.type,
              children: [],
            }))
          this.frontendLogger.info(`[BreadcrumbInput] Estructura cargada. Root: ${this.treeStructure?.name}, Folders hijos: ${this.currentChildren.length}`);
      })
    );
  }

  openCreateFolderDialog(templateRef: TemplateRef<any>) {
    this.createFolderError = ''; // Limpiar error previo
    const dialogRef = this.dialog.open(templateRef, {
      width: '350px'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.newFolderName = '';
      this.createFolderError = '';
    });
  }

  createFolder(dialogRef: MatDialogRef<any>) {
    if (this.newFolderName) {
      let lastNode = this.currentBreadcrumbs[this.currentBreadcrumbs.length - 1];
      const lastNodeId = lastNode.id; // Guardar el ID en lugar de la referencia
      this.createFolderError = '';

      this.documentService
          .createFolder({ 
            name: this.newFolderName, 
            parentId: lastNode.id, 
            contentType: ContentType.FOLDER,
            documentId: null
          })
          .pipe(
            tap(() => this.frontendLogger.info(`[BreadcrumbInput] Folder creado: ${this.newFolderName}`)),
            switchMap(() => this.getFolderStructure()),
            catchError((err) => {
              this.createFolderError = 'Error al crear la carpeta';
              this.frontendLogger.error(`[BreadcrumbInput] Error al crear folder: ${String(err)}`);
              return of(null);
            })
          )
          .subscribe(() => {
              if (!this.createFolderError) {
                // Refrescar usando el ID para encontrar el folder actualizado
                const path = this.findPath(this.treeStructure, lastNodeId);
                if (path.length) {
                  this.currentBreadcrumbs = path;
                  const last = path[path.length - 1];
                  this.currentChildren = (Array.isArray(last.children) ? last.children : [])
                    .filter(ch => this.isFolderType(ch?.type));
                }
                dialogRef.close();
                this.newFolderName = '';
              }
          });
    }
  }

  openFolder(folder: Content) {
    if (!folder || !this.treeStructure) { return; }
    const path = this.findPath(this.treeStructure, folder.id);

    if (path.length) {
      this.currentBreadcrumbs = path;
      const last = path[path.length - 1];
      this.currentChildren = (Array.isArray(last.children) ? last.children : [])
        .filter(ch => this.isFolderType(ch?.type));
    }
  }

  findPath(tree: Content, id: number): Content[] {
    if (!tree || id == null) return [];

    if (tree.id === id) {
      return [
        { 
          id: tree.id, 
          name: tree.name, 
          createdAt: tree.createdAt,
          type: tree.type,
          children: (Array.isArray(tree.children) ? tree.children : [])
            .filter(x => this.isFolderType(x?.type))
            .map((x:Content) => ({
              id: x?.id,
              name: x?.name,
              createdAt: x?.createdAt,
              type: x?.type,
              children: [],
            })),
        }];
    }

    if (Array.isArray(tree.children) && tree.children.length) {
      for (const child of tree.children) {
        const childPath = this.findPath(child, id);
        if (childPath.length) {
          return [{ 
              id: tree.id, 
              name: tree.name, 
              createdAt: tree.createdAt,
              type: tree.type,
              children: (Array.isArray(tree.children) ? tree.children : [])
                .filter(x => this.isFolderType(x?.type))
                .map((x:Content) => ({
                  id: x?.id,
                  name: x?.name,
                  createdAt: x?.createdAt,
                  type: x?.type,
                  children: [],
                })),
            }, 
            ...childPath
          ];
        }
      }
    }
    return [];
  }

  getCurrentFolderId(): number {
    const last = this.currentBreadcrumbs && this.currentBreadcrumbs.length
      ? this.currentBreadcrumbs[this.currentBreadcrumbs.length - 1]
      : null;
    return last?.id ?? 0;
  } 

  translate(key: string): string {
    return this.languageService.translate(key);
  }

  // --- Helpers: normalización de tipo y filtrado ---
  private isFolderType(t: any): boolean {
    return t === ContentType.FOLDER || t === 'FOLDER' || t === 'Folder' || t === 1;
  }

  private filterFoldersOnly(node: Content): Content {
    const children = Array.isArray(node.children) ? node.children : [];
    const onlyFolders = children.filter(c => this.isFolderType((c as any).type));
    return {
      id: node.id,
      name: node.name,
      createdAt: node.createdAt,
      type: node.type,
      children: onlyFolders.map(ch => this.filterFoldersOnly(ch as Content))
    } as Content;
  }

  reset(): void {
    // Resetear a la raíz del árbol
    if (this.treeStructure) {
      this.currentBreadcrumbs = [this.treeStructure];
      const children = Array.isArray(this.treeStructure?.children) ? this.treeStructure.children : [];
      this.currentChildren = children
        .filter(ch => this.isFolderType(ch?.type))
        .map((x: Content) => ({
          id: x?.id,
          name: x?.name,
          createdAt: x?.createdAt,
          type: x?.type,
          children: [],
        }));
    }
  }
  
}
